import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";

const Disclaimer = () => {
  const lastUpdated = "March 17, 2026";

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200">
      {/* Header */}
      <div className="bg-slate-800/80 border-b border-slate-700 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-400" />
            <span className="text-lg font-bold text-slate-100">Trading Journal</span>
          </div>
          <Link
            to="/login"
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-slate-100 mb-2">Investment Disclaimer</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: {lastUpdated}</p>

        <div className="space-y-8 text-slate-300 leading-relaxed">
          {/* Primary Disclaimer Box */}
          <section className="bg-red-900/20 border border-red-700/40 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-semibold text-red-400 mb-3">
                  Trading Involves Substantial Risk of Loss
                </h2>
                <p className="text-red-200/80">
                  Trading securities, options, and other financial instruments involves
                  substantial risk of loss and is not suitable for every investor. The
                  valuation of securities and derivatives may fluctuate, and as a result, you
                  may lose more than your original investment. The impact of seasonal and
                  geopolitical events is already factored into market prices. Past performance
                  is not indicative of future results.
                </p>
              </div>
            </div>
          </section>

          {/* Not an Advisor */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">Not a Registered Investment Adviser</h2>
            <p>
              Trading Journal is a personal trade tracking and analytics tool. It is <strong>NOT</strong>:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>A registered investment adviser under the Investment Advisers Act of 1940</li>
              <li>A broker-dealer registered with the SEC or FINRA</li>
              <li>A financial planner, tax advisor, or legal counsel</li>
              <li>A fiduciary acting in your best interest</li>
            </ul>
            <p className="mt-3">
              No content within the Service should be construed as a solicitation, recommendation,
              endorsement, or offer to buy or sell any securities or other financial instruments.
            </p>
          </section>

          {/* AI Disclaimer */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">AI-Generated Content Disclaimer</h2>
            <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-5">
              <p className="text-amber-200/80">
                The AI-powered features of Trading Journal, including but not limited to Edge
                Reports, Trade Validation, and AI Insights, are powered by third-party large
                language models (LLMs). These features:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-3 text-amber-200/80">
                <li>
                  <strong>Generate algorithmic outputs, not advice.</strong> AI analysis is based
                  on statistical patterns in your historical data and may not reflect real market
                  conditions or future outcomes.
                </li>
                <li>
                  <strong>May produce inaccurate or misleading results.</strong> LLMs can
                  "hallucinate" facts, misinterpret data, or produce logically flawed analysis.
                </li>
                <li>
                  <strong>Should never be the sole basis for trading decisions.</strong> Always
                  conduct your own research, analysis, and due diligence.
                </li>
                <li>
                  <strong>Are not personalized financial advice.</strong> The AI does not know
                  your financial situation, risk tolerance, investment goals, or tax circumstances.
                </li>
              </ul>
            </div>
          </section>

          {/* Data Accuracy */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">Data Accuracy</h2>
            <p>
              Trade data displayed in the Service is synced from third-party brokerage APIs and
              may contain:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Delays between actual execution and display in the Service</li>
              <li>Discrepancies due to API limitations, corporate actions, or data processing</li>
              <li>Rounding or calculation differences compared to your broker's records</li>
              <li>Missing or incomplete data for certain trade types or time periods</li>
            </ul>
            <p className="mt-3">
              The Service is not a system of record. Always verify important financial data
              directly with your brokerage.
            </p>
          </section>

          {/* Performance Metrics */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">Performance Metrics</h2>
            <p>
              Analytics, statistics, and performance metrics displayed in the Service are
              calculated based on available data and may not account for:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>All fees, commissions, and transaction costs</li>
              <li>Tax implications of your trades</li>
              <li>Slippage, market impact, or liquidity constraints</li>
              <li>The full context of complex multi-leg strategies</li>
            </ul>
            <p className="mt-3">
              Reported performance may differ from your actual investment returns. These metrics
              are provided for self-assessment purposes only.
            </p>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">Third-Party Services</h2>
            <p>
              Trading Journal integrates with third-party services including brokerage APIs,
              AI/LLM providers, and payment processors. We are not responsible for the
              availability, accuracy, or actions of these third-party services. Your use of
              third-party services is subject to their respective terms and policies.
            </p>
          </section>

          {/* Consult Professionals */}
          <section className="bg-emerald-900/20 border border-emerald-700/40 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-emerald-400 mb-3">Consult Qualified Professionals</h2>
            <p className="text-emerald-200/80">
              Before making any investment decisions, you should consult with:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-emerald-200/80">
              <li>A registered investment adviser or financial planner</li>
              <li>A tax professional regarding tax implications of your trades</li>
              <li>A legal professional for regulatory compliance questions</li>
            </ul>
            <p className="mt-3 text-emerald-200/80">
              Do not rely on this Service as a substitute for professional financial advice.
            </p>
          </section>

          {/* Acknowledgment */}
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">Your Acknowledgment</h2>
            <p>
              By using Trading Journal, you acknowledge that you have read and understood this
              disclaimer and that:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>You are solely responsible for your trading and investment decisions</li>
              <li>You understand the risks involved in trading securities</li>
              <li>You will not hold Trading Journal liable for any losses incurred</li>
              <li>AI-generated content is not financial advice and may be inaccurate</li>
              <li>Past performance does not guarantee future results</li>
            </ul>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-slate-700 flex flex-wrap gap-6 text-sm text-slate-500">
          <Link to="/privacy" className="hover:text-emerald-400 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-emerald-400 transition-colors">Terms of Service</Link>
          <Link to="/login" className="hover:text-emerald-400 transition-colors">Login</Link>
        </div>
      </div>
    </div>
  );
};

export default Disclaimer;
